/**
 * ExtractionPreviewPage — Sprint 23 F006
 *
 * Route: /financial/import/preview/:jobId?periodId=<uuid>
 *
 * Fetches the extraction job status (mapped data) from the API and renders
 * the ExtractionPreview component for user review and confirmation.
 */

import { useEffect, useState } from "react";
import { useRoute, useSearch } from "wouter";
import { Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ExtractionPreview, type ExtractedData } from "@/components/financial/import/extraction-preview";
import { apiRequest } from "@/lib/queryClient";

// Wouter navigate only accepts string | URL — helper to go back in history
function goBack(): void {
  window.history.back();
}

interface JobStatusResponse {
  jobId: string;
  status: string;
  provider?: string;
  result?: ExtractedData & {
    metadata?: {
      periodsDetected: string[];
      overallConfidence: number;
      totalLineItems: number;
      mappedCount: number;
      unmappedCount: number;
    };
  };
  error?: string;
}

export default function ExtractionPreviewPage() {
  const [, params] = useRoute('/financial/import/preview/:jobId');
  const search = useSearch();

  const jobId = params?.jobId ?? '';

  // Parse periodId from query string
  const periodId = new URLSearchParams(search ?? '').get('periodId') ?? '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
  const [provider, setProvider] = useState<string | undefined>();

  useEffect(() => {
    if (!jobId) {
      setError('No job ID provided.');
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchStatus() {
      try {
        const res = await apiRequest('GET', `/api/financial/extract/${jobId}/status`);
        const data: JobStatusResponse = await res.json();

        if (cancelled) return;

        if (data.status !== 'completed' || !data.result) {
          setError(
            data.error ??
              `Extraction is not yet complete (status: ${data.status}). Please wait and try again.`
          );
          setLoading(false);
          return;
        }

        const result = data.result;
        setProvider(data.provider);

        // Normalise: ensure all required fields are present
        const normalised: ExtractedData = {
          profitLoss: (result.profitLoss ?? []).map((item) => ({
            lineItemType: item.lineItemType,
            label: item.label ?? String(item.lineItemType),
            sourceLabel: item.sourceLabel ?? String(item.lineItemType),
            amount: typeof item.amount === 'number' ? item.amount : Number(item.amount ?? 0),
            confidence: typeof item.confidence === 'number' ? item.confidence : Number(item.confidence ?? 0),
            matchType: item.matchType ?? 'exact',
            values: item.values,
          })),
          balanceSheet: (result.balanceSheet ?? []).map((item) => ({
            lineItemType: item.lineItemType,
            label: item.label ?? String(item.lineItemType),
            sourceLabel: item.sourceLabel ?? String(item.lineItemType),
            amount: typeof item.amount === 'number' ? item.amount : Number(item.amount ?? 0),
            confidence: typeof item.confidence === 'number' ? item.confidence : Number(item.confidence ?? 0),
            matchType: item.matchType ?? 'exact',
            values: item.values,
          })),
          unmapped: result.unmapped ?? [],
          metadata: {
            periodsDetected: result.metadata?.periodsDetected ?? [],
            overallConfidence: result.metadata?.overallConfidence ?? 0,
            totalLineItems: result.metadata?.totalLineItems ?? 0,
            mappedCount: result.metadata?.mappedCount ?? 0,
            unmappedCount: result.metadata?.unmappedCount ?? 0,
            provider: data.provider,
            sourceFileName: undefined,
          },
        };

        setExtractedData(normalised);
      } catch (err: unknown) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load extraction data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void fetchStatus();
    return () => { cancelled = true; };
  }, [jobId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading extracted data...</p>
        </div>
      </div>
    );
  }

  if (error || !extractedData) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="flex flex-col items-center gap-3 max-w-md text-center">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <p className="text-sm text-destructive">{error ?? 'No extraction data available.'}</p>
          <Button variant="outline" size="sm" onClick={goBack}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-4xl mx-auto py-6 px-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Extraction Preview</CardTitle>
          {provider && (
            <p className="text-xs text-muted-foreground">Provider: {provider}</p>
          )}
        </CardHeader>
        <CardContent>
          <ExtractionPreview
            jobId={jobId}
            financialPeriodId={periodId}
            extractedData={extractedData}
            onComplete={() => {
              // Navigate back to the financial period page after successful import
              goBack();
            }}
            onCancel={goBack}
          />
        </CardContent>
      </Card>
    </div>
  );
}
